import React, { createContext, useContext, /*useReducer, */ useState } from 'react';
import {
  // AuthToken,
  User,
  Booking,
  CoveredAreas,
  DayAvailability,
  Availability,
  PartnerAvailability,
  UserRating,
  Income,
} from '../types';
// import { TwilioError } from 'twilio-video';
// import { settingsReducer, initialSettings, Settings, SettingsAction } from './settings/settingsReducer';
// import useActiveSinkId from './useActiveSinkId/useActiveSinkId';
// import useFirebaseAuth from './useFirebaseAuth/useFirebaseAuth';
// import { useLocalStorageState } from '../hooks/useLocalStorageState/useLocalStorageState';
// import usePasscodeAuth from './usePasscodeAuth/usePasscodeAuth';
import useAuth from './useAuth/useAuth';
// import { User } from 'firebase/auth';
// import { process.env.REACT_APP_API_BASE_URL } from '../constants';

import moment from 'moment';

export interface StateContextType {
  error: /*TwilioError | */ Error | null;
  setError(error: /*TwilioError | */ Error | null): void;
  getBookings(params: any): Promise<{ content: Booking[]; totalElements: number }>;
  getUpcomingBookings(params?: any): Promise<{ data: { content: Booking[]; totalElements: number } }>;
  getInProgressBookings(params?: any): Promise<{ data: { content: Booking[]; totalElements: number } }>;
  getCompletedBookings(params?: any): Promise<{ data: { content: Booking[]; totalElements: number } }>;
  getBooking(bookingId: number, isPartner?: boolean): Promise<{ data: Booking }>;
  getIncome(
    partnerId: string
  ): Promise<{ data: { currency: string; monthValue: number; yearValue: number; symbol: string } }>;
  // getToken(name: string, room: string, passcode?: string): Promise<{ room_type: RoomType; token: string }>;
  getPartnerContacts(partnerId: string): Promise<{ data: { email: string; phoneCode: string; phoneNumber: string } }>;
  user?: null | User;
  setUser(user: User): void;
  userEmail: string;
  setBooking(booking: Booking | null): void;
  incomeInfo: Income;
  setIncomeInfo(income: Income | null): void;
  booking: Booking;
  bookings?: Booking[];
  upcomingBookings?: Booking[] | null;
  inProgressBookings?: Booking[] | null;
  completedBookings?: Booking[] | null;
  numberOfCompletedBookings?: number;
  hasMoreBookings?: boolean;
  pageLoaded?: number;
  dayAvailability?: { hash: string | number; content: Availability /*Availability[]*/ };
  bookedTimeSlots?: Availability; //Availability[];
  locations?: string[];
  // getBookedTimeSlots?: { hash: string | number; content: Availability[] };
  setDayAvailability: React.Dispatch<
    React.SetStateAction<{ hash: string | number; content: Availability /*Availability[]*/ }>
  >;
  signIn?(phoneCode: string, phoneNumber: string, password: string): Promise<User>;
  signOut?(): Promise<void>;
  isAuthReady?: boolean;
  isFetching: boolean;
  isBookingsFetching?: boolean;
  isAvailabilityFetching: boolean; // We need different flags otherwise,
  isAvailabilityLoading: boolean; // pop-up overlay from Availability will be shown
  isAvailabilityLoaded: boolean; // for getBookedTimeSlots to know when to hide the loader
  // activeSinkId: string;
  // setActiveSinkId(sinkId: string): void;
  // settings: Settings;
  // dispatchSetting: React.Dispatch<SettingsAction>;
  // roomType?: RoomType;
  // updateRecordingRules(room_sid: string, rules: RecordingRules): Promise<object>;
  loadPartnerCoverage(partnerId: string): Promise<{ data: string[] }>;
  updatePartnerCoverage(partnerId: string, areas: CoveredAreas): Promise<object>;
  updatePartner(partnerId: string, data: any): Promise<object>;
  runningLate(partnerId: string, bookingId: number, delayInMinutes?: number): Promise<object>;
  // setRunningLate(partnerId: string, bookingId: string, delayInMinutes?: number): Promise<object>;
  rateCustomer(
    partnerId: number | string,
    // customerId: number | string,
    bookingId: number,
    // strength: number,
    // endurance: number,
    // fitness: number,
    // overall: number,
    comment: string
  ): Promise<{ data: Booking }>;
  startSession(partnerId: string, bookingId: number): Promise<object>;
  stopSession(partnerId: string, bookingId: number): Promise<object>;
  declineSession(partnerId: string, bookingId: number): Promise<object>;
  cancelSession(partnerId: string, bookingId: number): Promise<object>;
  getAvailability(partnerId: string): Promise<{ data: PartnerAvailability[] }>;
  getBookedTimeSlots(partnerId: string): Promise<{ data: PartnerAvailability[] }>;
  updateAvailability(partnerId: string, timeslots: PartnerAvailability[]): Promise<void>;
  recoverPassword(email: string): Promise<void>;
  code?: number | string;
  passwordReset: boolean;
  sendResetCode(phoneCode: string, phoneNumber: string): Promise<void>;
  verifyPhoneNumberPasswordReset(phoneCode: string, phoneNumber: string, code: string): Promise<void>;
  resetPassword(phoneCode: string, phoneNumber: string, password: string): Promise<void>;
  clearCompletedBookingsLoaded(): void;
  isPasswordResetReady: boolean;
  // setBookingCancelled: React.Dispatch<React.SetStateAction<boolean>>;
  // isGalleryViewActive: boolean;
  // setIsGalleryViewActive: React.Dispatch<React.SetStateAction<boolean>>;
  // maxGalleryViewParticipants: number;
  // setMaxGalleryViewParticipants: React.Dispatch<React.SetStateAction<number>>;
}

export const StateContext = createContext<StateContextType>(null!);

/*
  The 'react-hooks/rules-of-hooks' linting rules prevent React Hooks from being called
  inside of if() statements. This is because hooks must always be called in the same order
  every time a component is rendered. The 'react-hooks/rules-of-hooks' rule is disabled below
  because the "if (process.env.REACT_APP_SET_AUTH === 'firebase')" statements are evaluated
  at build time (not runtime). If the statement evaluates to false, then the code is not
  included in the bundle that is produced (due to tree-shaking). Thus, in this instance, it
  is ok to call hooks inside if() statements.
*/
export default function AppStateProvider(props: React.PropsWithChildren<{}>) {
  const [error, setError] = useState</*Twilio*/ Error | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isBookingsFetching, setIsBookingsFetching] = useState<boolean>(false);
  const [isAvailabilityFetching, setIsAvailabilityFetching] = useState<boolean>(false);
  const [isAvailabilityLoading, setIsAvailabilityLoading] = useState<boolean>(false);
  const [isAvailabilityLoaded, setIsAvailabilityLoaded] = useState<boolean>(false);

  // const [isGalleryViewActive, setIsGalleryViewActive] = useLocalStorageState('gallery-view-active-key', true);
  // const [activeSinkId, setActiveSinkId] = useActiveSinkId();
  // const [settings, dispatchSetting] = useReducer(settingsReducer, initialSettings);
  // const [roomType, setRoomType] = useState<RoomType>();

  // Not used
  const [bookings] = useState<Booking[] | null>([]);

  // const [authToken, setAuthToken] = useState('');
  const [booking, setBooking] = useState<Booking>();
  // const [booking, setBooking] = useState<{ twilioRoomName: string; twilioAccessToken: string }>();
  // const [bookings, setBookings] = useState<Booking[]>([]);
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[] | null>([]);
  const [inProgressBookings, setInProgressBookings] = useState<Booking[] | null>([]);
  const [completedBookings, setCompletedBookings] = useState<Booking[] | null>([]);
  const [completedBookingsLoaded, setCompletedBookingsLoaded] = useState(false);
  const [numberOfCompletedBookings, setNumberOfCompletedBookings] = useState(0);
  const [hasMoreBookings, setHasMoreBookings] = useState<boolean>(false);
  const [pageLoaded, setPageLoaded] = useState(-1);
  const [incomeInfo, setIncomeInfo] = useState<Income | null>(null);
  const [dayAvailability, setDayAvailability] = useState<{
    hash: string | number;
    content: Availability /*Availability[]*/;
  }>({
    hash: Math.random(),
    content: {},
  });

  const [bookedTimeSlots, setBookedTimeSlots] = useState<Availability>({ '2023-04-20T10:30:00.000Z': undefined });
  const [passwordReset, setPasswordReset] = useState<boolean>(false);
  const [locations, setLocations] = useState<string[]>([]);
  // const [code, setCode] = useState<number | string | null>(null);
  // const [maxGalleryViewParticipants, setMaxGalleryViewParticipants] = useLocalStorageState(
  //   'max-gallery-participants-key',
  //   6
  // );

  let contextValue = {
    error,
    setError,
    isFetching,
    isBookingsFetching,
    isAvailabilityFetching,
    isAvailabilityLoading,
    isAvailabilityLoaded,
    setBooking,
    // getBooking,
    // getBookings,
    // getUpcomingBookings,
    // getInProgressBookings,
    // updatePartnerCoverage,
    // activeSinkId,
    // setActiveSinkId,
    booking,
    bookings,
    upcomingBookings,
    inProgressBookings,
    completedBookings,
    numberOfCompletedBookings,
    pageLoaded,
    hasMoreBookings,
    incomeInfo,
    dayAvailability,
    setDayAvailability,
    bookedTimeSlots,
    // settings,
    // dispatchSetting,
    // roomType,
    // code,
    passwordReset,
    // isGalleryViewActive,
    // setIsGalleryViewActive,
    // maxGalleryViewParticipants,
    // setMaxGalleryViewParticipants,
  } as StateContextType;

  const completedBookingsRef = React.useRef<Booking[]>([]);
  const completedBookingsPageLoadedRef = React.useRef<number>(-1);

  // console.log('dayAvailability', dayAvailability);

  // if (process.env.REACT_APP_SET_AUTH === 'firebase') {
  //   contextValue = {
  //     ...contextValue,
  //     ...useFirebaseAuth(), // eslint-disable-line react-hooks/rules-of-hooks
  //   };
  // } else if (process.env.REACT_APP_SET_AUTH === 'passcode') {
  //   contextValue = {
  //     ...contextValue,
  //     ...usePasscodeAuth(), // eslint-disable-line react-hooks/rules-of-hooks
  //   };
  // } else {
  contextValue = {
    ...contextValue,
    ...useAuth(), // eslint-disable-line react-hooks/rules-of-hooks
    // Move to separate module
    loadPartnerCoverage: async (partnerId: string) => {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/locations/v1/locations?partnerId=${partnerId}`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const recordingError = new Error(jsonResponse.message || 'There was an error fetching partner coverage');
          // recordingError.code = jsonResponse.error?.code;
          return Promise.reject(recordingError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },
    // Move to separate module
    updatePartnerCoverage: async (partnerId: string, areas: CoveredAreas) => {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/locations/v1/locations`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          partnerId,
          codes: areas,
        }),
        method: 'PUT',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const recordingError = new Error(jsonResponse.message || 'There was an error updating partner coverage');
          // recordingError.code = jsonResponse.error?.code;
          return Promise.reject(recordingError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },
    updatePartner: async (partnerId, data) => {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/profiles/v1/partners/${partnerId}`;
      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
        method: 'PATCH',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const error = new Error(jsonResponse.error?.message || 'There was an error updating partner info');
          error.code = jsonResponse.error?.code;

          return Promise.reject(error);
        }

        return jsonResponse;
      });
    },
    // setRunningLate: async (partnerId, bookingId) => {
    runningLate: async (partnerId: string, bookingId: number, delayInMinutes?: number) => {
      // POST /partners/{partnerId}/bookings/{bookingId}/running-late
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/partners/${partnerId}/bookings/${bookingId}/running-late`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          delayInMinutes: delayInMinutes || 15,
        }),
        method: 'POST',
      }).then(async res => {
        // const jsonResponse = await res.json(); // Empty response

        if (!res.ok) {
          const runningLateError = new Error('There was an error fetching running late');
          // @ts-ignore
          runningLateError.code = res.status;

          return Promise.reject(runningLateError);
        }

        return Promise.resolve(res);

        // if (!res.ok) {
        //   const recordingError = new Error(
        //     jsonResponse.error?.message ||
        //   );
        //   recordingError.code = jsonResponse.error?.code;
        //   return Promise.reject(recordingError);
        // }

        // return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    // Create partner review
    rateCustomer(
      partnerId: number | string,
      // customerId: number | string,
      bookingId: number,
      // strength: number,
      // endurance: number,
      // fitness: number,
      // overall: number,
      comment: string
    ) {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/bookings/v1/bookings/${bookingId}/review?partnerId=${partnerId}&review=${comment}`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify({
        //   // average: 0,
        //   // rating: overall,
        //   review: comment,
        //   customerId: customerId,
        //   bookingId: bookingId,
        //   // strengthRating: strength,
        //   // enduranceRating: endurance,
        //   // fitnessRating: fitness,
        //   // overallExperienceRating: overall,
        //   // comment,
        // }),
        method: 'POST',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const rateCustomerError = new Error(
            jsonResponse.error?.message || 'There was an error setting customer rating'
          );
          rateCustomerError.code = jsonResponse.status;
          rateCustomerError.message = jsonResponse.message;

          return Promise.reject(rateCustomerError);
        }

        // if (!res.ok) {
        //   const recordingError = new Error(
        //     jsonResponse.error?.message ||
        //   );
        //   recordingError.code = jsonResponse.error?.code;
        //   return Promise.reject(recordingError);
        // }

        return jsonResponse;
      });
      // .catch(err => {
      //   setError(err);

      //   return Promise.reject(err);
      // });

      // PartnerRateCustomerSessionHUCRequest request = new PartnerRateCustomerSessionHUCRequest(mapper, partner.getAuthToken().getId(), partner.getId(),
      //         getId(), strength, endurance, fitness, overall, comment);
      // Booking.PartnerRating rating = client.executeAndGetAs(request, PartnerRateCustomerSessionHUCResponse.class).get();
      // setPartnerRating(rating);
    },

    startSession(partnerId: string, bookingId: number) {
      // PartnerStartSessionHUCRequest req = new PartnerStartSessionHUCRequest(mapper,  partner.getAuthToken().getId(),partner.getId(), getId());
      // Booking booking = client.executeAndGetAs(req, PartnerStartSessionHUCResponse.class).get();
      // this.setStatus(booking.getStatus());
      // return booking;

      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/bookings/v1/bookings/${bookingId}/start?partnerId=${partnerId}`;

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify({
        // }),
        method: 'POST',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const startSessionError = new Error(jsonResponse.error?.message || 'There was an error starting a session');
          // startSessionError.code = jsonResponse.error?.message;
          return Promise.reject(startSessionError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    stopSession(partnerId: string, bookingId: number) {
      // PartnerStartSessionHUCRequest req = new PartnerStartSessionHUCRequest(mapper,  partner.getAuthToken().getId(),partner.getId(), getId());
      // Booking booking = client.executeAndGetAs(req, PartnerStartSessionHUCResponse.class).get();
      // this.setStatus(booking.getStatus());
      // return booking;

      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/bookings/v1/bookings/${bookingId}/complete?partnerId=${partnerId}`;

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify({
        // }),
        method: 'POST',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const completeSessionError = new Error(jsonResponse.message || 'There was an error completing a session');
          // completeSessionError.code = jsonResponse.message;
          return Promise.reject(completeSessionError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    declineSession(partnerId: string, bookingId: number) {
      // PartnerStartSessionHUCRequest req = new PartnerStartSessionHUCRequest(mapper,  partner.getAuthToken().getId(),partner.getId(), getId());
      // Booking booking = client.executeAndGetAs(req, PartnerStartSessionHUCResponse.class).get();
      // this.setStatus(booking.getStatus());
      // return booking;

      // /partners/"+partnerId+"/bookings/"+bookingId+"/session-declined
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/partners/${partnerId}/bookings/${bookingId}/session-declined`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify({
        // }),
        method: 'POST',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const declineSessionError = new Error(
            jsonResponse.error?.message || 'There was an error declining a session'
          );
          declineSessionError.code = jsonResponse.error?.message;
          return Promise.reject(declineSessionError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    cancelSession(partnerId: string, bookingId: number) {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/partners/${partnerId}/bookings/${bookingId}/cancellation`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify({
        // }),
        method: 'POST',
      }).then(async res => {
        // const jsonResponse = await res.json(); // Empty response

        if (!res.ok) {
          const cencelSessionError = new Error('There was an error cancelling a session');
          // @ts-ignore
          cencelSessionError.code = res.status;

          return Promise.reject(cencelSessionError);
        }

        return Promise.resolve(res);
      });
      // .catch(err => setError(err));
    },

    getAvailability(partnerId: string) {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/calendars/v1/timeslots?partnerId=${partnerId}`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      // const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          // Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify({
        // }),
        // method: 'POST',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const getAvailabilityError = new Error(
            jsonResponse.message || 'There was an error loading partner availability'
          );
          // getAvailabilityError.code = jsonResponse.error?.message;
          return Promise.reject(getAvailabilityError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    getBookedTimeSlots(partnerId: string) {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/calendars/v1/timeslots/booked?partnerId=${partnerId}`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      // const token = window.localStorage.getItem('accessToken') || '';

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          // Authorization: `Bearer ${token}`,
        },
        // body: JSON.stringify({
        // }),
        // method: 'POST',
      }).then(async res => {
        const jsonResponse = await res.json();

        if (!res.ok) {
          const getAvailabilityError = new Error(
            jsonResponse.message || 'There was an error loading partner booked time slots'
          );
          // getAvailabilityError.code = jsonResponse.error?.message;
          return Promise.reject(getAvailabilityError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    /**
     * In the following format
     *  {
     *    "timeslots": [
     *      {
     *        "start": "string",
     *        "active": "boolean"
     *      }
     *    ]
     *  }
     */
    // eslint-disable-next-line @typescript-eslint/no-shadow
    updateAvailability(partnerId: string, timeslots: PartnerAvailability[]) {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/calendars/v1/timeslots`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      const token = window.localStorage.getItem('accessToken') || '';

      if (!timeslots) {
        return Promise.reject();
      }

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          partnerId,
          timeslots,
          // dayAvailability
          // dayAvailability.map(v => {
          //   return {
          //     day: v.day,
          //     timeSlots: v.timeSlots.map(ts => ({ startTime: ts.startTime }))
          //   };
          // })
        }),
        method: 'PATCH',
      }).then(async res => {
        const jsonResponse = Promise.resolve(); // = await res.json();

        if (!res.ok) {
          const updateAvailabilityError = new Error('There was an error updating availability');
          // updateAvailabilityError.code = jsonResponse?.message;
          return Promise.reject(updateAvailabilityError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    /* Customer recovers password */
    recoverPassword(email: string) {
      const endpointUrl = `${process.env.REACT_APP_API_BASE_URL}/users/forgotten-password`; //process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

      window.localStorage.setItem('password_recovery_email', email);

      return fetch(endpointUrl, {
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
        }),
        method: 'POST',
      }).then(async res => {
        const jsonResponse = Promise.resolve(); //await res.json();

        if (!res.ok) {
          const recoverPasswordError = new Error(
            /*jsonResponse.message || */ 'There was an error recovering the password'
          );
          // recoverPasswordError.code = jsonResponse.message;
          return Promise.reject(recoverPasswordError);
        }

        return jsonResponse;
      });
      // .catch(err => setError(err));
    },

    // public Booking stopSession(Partner partner) {
    //     PartnerStopSessionHUCRequest req = new PartnerStopSessionHUCRequest(mapper, partner.getAuthToken().getId(), partner.getId() , getId());
    //     Booking booking =  client.executeAndGetAs(req, PartnerStopSessionHUCResponse.class).get();
    //     this.setStatus(booking.getStatus());
    //     return booking;
    // }

    // public void declineSession(Partner partner, String reason) {

    //     PartnerDeclineSessionHUCRequest req = new PartnerDeclineSessionHUCRequest(mapper, partner.getAuthToken().getId(), partner.getId(), getId(), reason);
    //     client.execute(req);
    // }

    // public void cancelSession(Partner partner) {
    //     PartnerCancelSessionHUCRequest req = new PartnerCancelSessionHUCRequest(mapper, partner.getAuthToken().getId(), partner.getId() , getId());
    //     client.execute(req);
    // }
  };
  // }// else {
  // contextValue = {
  //   ...contextValue,
  //   signIn: async (email, password) => {
  //     const endpoint = `${process.env.REACT_APP_API_BASE_URL}/users/auth`; // process.env.REACT_APP_TOKEN_ENDPOINT || '/token';

  //     return fetch(endpoint, {
  //       method: 'POST',
  //       headers: {
  //         'content-type': 'application/json',
  //       },
  //       body: JSON.stringify({
  //         email,
  //         password,
  //         // create_conversation: process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true',
  //       }),
  //     }).then(res => res.json())
  //   },
  //   getBooking: async (userId: string, bookingId: string, isPartner?: boolean) => {
  //     const endpoint = `${process.env.REACT_APP_API_BASE_URL}/users/${userId}/bookings/${bookingId}`; // process.env.REACT_APP_TOKEN_ENDPOINT || '/token';

  //     return fetch(endpoint, {
  //       method: 'GET',
  //       headers: {
  //         'content-type': 'application/json',
  //         Authorization: `Bearer ${token}`,
  //       },
  //       // body: JSON.stringify({
  //       //   user_identity,
  //       //   room_name,
  //       //   create_conversation: process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true',
  //       // }),
  //     }).then(res => res.json());
  //   },
  //   getToken: async (user_identity, room_name) => {
  //     const endpoint = process.env.REACT_APP_TOKEN_ENDPOINT || '/token';

  //     return fetch(endpoint, {
  //       method: 'POST',
  //       headers: {
  //         'content-type': 'application/json',
  //       },
  //       body: JSON.stringify({
  //         user_identity,
  //         room_name,
  //         create_conversation: process.env.REACT_APP_DISABLE_TWILIO_CONVERSATIONS !== 'true',
  //       }),
  //     }).then(res => res.json());
  //   },
  //   updateRecordingRules: async (room_sid, rules) => {
  //     const endpoint = process.env.REACT_APP_TOKEN_ENDPOINT || '/recordingrules';

  //     return fetch(endpoint, {
  //       headers: {
  //         'Content-Type': 'application/json',
  //       },
  //       body: JSON.stringify({ room_sid, rules }),
  //       method: 'POST',
  //     })
  //       .then(async res => {
  //         const jsonResponse = await res.json();

  //         if (!res.ok) {
  //           const recordingError = new Error(
  //             jsonResponse.error?.message || 'There was an error updating recording rules'
  //           );
  //           recordingError.code = jsonResponse.error?.code;
  //           return Promise.reject(recordingError);
  //         }

  //         return jsonResponse;
  //       })
  //       .catch(err => setError(err));
  //   },
  // };
  // }

  // const getToken: StateContextType['getToken'] = (name, room) => {
  //   setIsFetching(true);
  //   return contextValue
  //     .getToken(name, room)
  //     .then(res => {
  //       setRoomType(res.room_type);
  //       setIsFetching(false);
  //       return res;
  //     })
  //     .catch(err => {
  //       setError(err);
  //       setIsFetching(false);
  //       return Promise.reject(err);
  //     });
  // };

  const getBooking: StateContextType['getBooking'] = (bookingId, isPartner) => {
    setIsFetching(true);
    return contextValue
      .getBooking(bookingId, isPartner)
      .then(res => {
        setBooking(res.data as Booking);
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  // Not used
  // const getBookings: StateContextType['getBookings'] = (params) => {
  //   setIsFetching(true);
  //   setIsBookingsFetching(true);
  //   return contextValue
  //     .getBookings(params)
  //     .then(res => {
  //       setBookings(res.content as Booking[]);
  //       setIsFetching(false);
  //       setIsBookingsFetching(false);
  //       return res;
  //     })
  //     .catch(err => {
  //       setError(err);
  //       setIsFetching(false);
  //       setIsBookingsFetching(false);
  //       return Promise.reject(err);
  //     });
  // };

  const getUpcomingBookings: StateContextType['getUpcomingBookings'] = (params?) => {
    setIsFetching(true);
    setIsBookingsFetching(true);
    return contextValue
      .getUpcomingBookings(params)
      .then(res => {
        setUpcomingBookings(res.data.content as Booking[]);
        setIsFetching(false);
        setIsBookingsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        setIsBookingsFetching(false);
        return Promise.reject(err);
      });
  };

  const getInProgressBookings: StateContextType['getInProgressBookings'] = (params?) => {
    setIsFetching(true);
    setIsBookingsFetching(true);
    return contextValue
      .getInProgressBookings(params)
      .then(res => {
        setInProgressBookings(res.data.content as Booking[]);
        setIsFetching(false);
        setIsBookingsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        setIsBookingsFetching(false);
        return Promise.reject(err);
      });
  };

  const getCompletedBookings: StateContextType['getCompletedBookings'] = (params?) => {
    setIsFetching(true);
    setIsBookingsFetching(true);

    return contextValue
      .getCompletedBookings(params)
      .then(res => {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        // let bookings: Booking[] = res.data.content as Booking[];
        // let hasMore: boolean = false;
        let page = params?.page || 0;
        let pastBookings: Booking[] = page <= 0 ? [] : completedBookingsRef.current;

        let completedBookingsOffset = pastBookings.length || 0;
        let newCompletedBookingsOffset = completedBookingsOffset + res.data.content.length;

        if (completedBookingsOffset < newCompletedBookingsOffset) {
          // new items
          pastBookings = [
            ...pastBookings,
            ...res.data.content.slice(-(newCompletedBookingsOffset - completedBookingsOffset)),
          ];
        }

        let hasMore = newCompletedBookingsOffset < res.data.totalElements;
        page = hasMore ? page : completedBookingsPageLoadedRef.current;

        completedBookingsRef.current = pastBookings;

        // return Object.assign({}, state, { isCompletedFetching: false, isCompletedReceived: true }, { completed: { content: bookings, hasMore, page } });
        setHasMoreBookings(hasMore);
        setPageLoaded(page);
        completedBookingsPageLoadedRef.current = page;
        setNumberOfCompletedBookings(res.data.totalElements);

        if (pastBookings) {
          setCompletedBookings(pastBookings);
          setCompletedBookingsLoaded(true);
        }

        setIsFetching(false);
        setIsBookingsFetching(false);

        return res;
      })
      .catch(err => {
        setError(err);
        return Promise.reject(err);
      })
      .finally(() => {
        setIsFetching(false);
        setIsBookingsFetching(false);
      });
  };

  const getIncome: StateContextType['getIncome'] = (partnerId: string) => {
    setIsFetching(true);

    return contextValue
      .getIncome(partnerId)
      .then(res => {
        setIncomeInfo(res.data as Income);
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  // const updateRecordingRules: StateContextType['updateRecordingRules'] = (room_sid, rules) => {
  //   setIsFetching(true);
  //   return contextValue
  //     .updateRecordingRules(room_sid, rules)
  //     .then(res => {
  //       setIsFetching(false);
  //       return res;
  //     })
  //     .catch(err => {
  //       setError(err);
  //       setIsFetching(false);
  //       return Promise.reject(err);
  //     });
  // };

  const loadPartnerCoverage: StateContextType['loadPartnerCoverage'] = partnerId => {
    setIsFetching(true);
    return contextValue
      .loadPartnerCoverage(partnerId)
      .then(res => {
        setLocations(res.data);
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const updatePartnerCoverage: StateContextType['updatePartnerCoverage'] = (partnerId, areas) => {
    setIsFetching(true);
    return contextValue
      .updatePartnerCoverage(partnerId, areas)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const updatePartner: StateContextType['updatePartner'] = (partnerId, data) => {
    setIsFetching(true);
    return contextValue
      .updatePartner(partnerId, data)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const runningLate: StateContextType['runningLate'] = (
    partnerId: string,
    bookingId: number,
    delayInMinutes?: number
  ) => {
    // const runningLate: StateContextType['setRunningLate'] = (partnerId: string, bookingId: number) => {
    setIsFetching(true);
    return contextValue
      .runningLate(partnerId, bookingId, delayInMinutes)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const rateCustomer: StateContextType['rateCustomer'] = (
    partnerId: number | string,
    // customerId: number | string,
    bookingId: number,
    // strength: number,
    // endurance: number,
    // fitness: number,
    // overall: number,
    comment: string
  ) => {
    // const addCustomerRating: StateContextType['addCustomerRating'] = (partnerId: string, bookingId: number, strength: number, endurance: number, fitness: number, overall: number, comment: string) => {
    setIsFetching(true);

    return contextValue
      .rateCustomer(partnerId, bookingId, /*strength, endurance, fitness, overall, */ comment)
      .then(res => {
        setIsFetching(false);
        setBooking(res.data as Booking);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const startSession: StateContextType['startSession'] = (partnerId: string, bookingId: number) => {
    setIsFetching(true);
    return contextValue
      .startSession(partnerId, bookingId)
      .then(res => {
        // setBooking(res as Booking);
        setBooking({
          ...booking!,
          status: 'ACTIVE',
        });
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const stopSession: StateContextType['stopSession'] = (partnerId: string, bookingId: number) => {
    setIsFetching(true);
    return contextValue
      .stopSession(partnerId, bookingId)
      .then(res => {
        // setBooking(res as Booking);
        setBooking({
          ...booking!,
          status: 'COMPLETED',
        });
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const declineSession: StateContextType['declineSession'] = (partnerId: string, bookingId: number) => {
    setIsFetching(true);
    return contextValue
      .declineSession(partnerId, bookingId)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const cancelSession: StateContextType['cancelSession'] = (partnerId: string, bookingId: number) => {
    setIsFetching(true);
    return contextValue
      .cancelSession(partnerId, bookingId)
      .then(res => {
        setIsFetching(false);
        setBooking({
          ...booking!,
          status: 'CANCELLED_PARTNER_REFUNDED',
        });
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const getAvailability: StateContextType['getAvailability'] = (partnerId: string) => {
    // setIsFetching(true);
    // setIsAvailabilityFetching(true); // to prevent showing loader on AvailabilityDayPickerView
    setIsAvailabilityLoading(true);
    return contextValue
      .getAvailability(partnerId)
      .then(res => {
        setDayAvailability({
          hash: Math.random(),
          content: res.data.reduce((acc: Availability, val: PartnerAvailability) => {
            acc[moment.utc(val.start).toISOString()] = undefined;
            return acc;
          }, {} as Availability) /* as Availability[]*/,
        });
        // setIsFetching(false);
        // setIsAvailabilityFetching(false);
        setIsAvailabilityLoading(false);
        return res;
      })
      .catch(err => {
        setError(err);
        // setIsFetching(false);
        // setIsAvailabilityFetching(false);
        setIsAvailabilityLoading(false);
        return Promise.reject(err);
      });
  };

  const getBookedTimeSlots: StateContextType['getBookedTimeSlots'] = (partnerId: string) => {
    setIsAvailabilityLoading(true);
    return contextValue
      .getBookedTimeSlots(partnerId)
      .then(res => {
        setBookedTimeSlots(
          res.data.reduce((acc: Availability, val: PartnerAvailability) => {
            acc[moment.utc(val.start).toISOString()] = undefined;
            return acc;
          }, {} as Availability) /* as Availability[]*/
        );
        // Wait for getAvailability
        if (isAvailabilityLoading && isAvailabilityLoaded) {
          setIsAvailabilityLoading(false);
        }
        return res;
      })
      .catch(err => {
        setError(err);
        return Promise.reject(err);
      })
      .finally(() => {
        // Wait for getAvailability
        if (isAvailabilityLoading && isAvailabilityLoaded) {
          setIsAvailabilityLoading(false);
        }
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const updateAvailability: StateContextType['updateAvailability'] = (
    partnerId: string,
    dayAvailability: PartnerAvailability[]
  ) => {
    // setIsFetching(true);
    setIsAvailabilityFetching(true);
    return contextValue
      .updateAvailability(partnerId, dayAvailability)
      .then(res => {
        // setIsFetching(false);
        setIsAvailabilityFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        // setIsFetching(false);
        setIsAvailabilityFetching(false);
        return Promise.reject(err);
      });
  };

  const recoverPassword: StateContextType['recoverPassword'] = (email: string) => {
    setIsFetching(true);
    return contextValue
      .recoverPassword(email)
      .then(res => {
        // setCode(res);
        setPasswordReset(true);
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const sendResetCode: StateContextType['sendResetCode'] = (phoneCode: string, phoneNumber: string) => {
    setIsFetching(true);
    return contextValue
      .sendResetCode(phoneCode, phoneNumber)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const resetPassword: StateContextType['resetPassword'] = (
    phoneCode: string,
    phoneNumber: string,
    password: string
  ) => {
    setIsFetching(true);
    return contextValue
      .resetPassword(phoneCode, phoneNumber, password)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  const verifyPhoneNumberPasswordReset: StateContextType['verifyPhoneNumberPasswordReset'] = (
    phoneCode: string,
    phoneNumber: string,
    code: string
  ) => {
    setIsFetching(true);
    return contextValue
      .verifyPhoneNumberPasswordReset(phoneCode, phoneNumber, code)
      .then(res => {
        setIsFetching(false);
        return res;
      })
      .catch(err => {
        setError(err);
        setIsFetching(false);
        return Promise.reject(err);
      });
  };

  // const signIn: StateContextType['signIn'] = (email, password) => {
  //   setIsFetching(true);
  //   return contextValue
  //     .signIn(email, password)
  //     .then(res => {
  //       setAuthToken(res?.authToken?.id);
  //       setIsFetching(false);
  //       return res;
  //     })
  //     .catch(err => {
  //       setError(err);
  //       setIsFetching(false);
  //       return Promise.reject(err);
  //     });
  // };

  // const signIn: StateContextType['signIn'] = (email, password) => {
  //   setIsFetching(true);
  //   return contextValue
  //     .signIn(email, password)
  //     .then(res => {
  //       setAuthToken(res?.authToken?.id);
  //       setIsFetching(false);
  //       return res;
  //     })
  //     .catch(err => {
  //       setError(err);
  //       setIsFetching(false);
  //       return Promise.reject(err);
  //     });
  // };

  /**
   * Prevent past bookings caching
   */
  const clearCompletedBookingsLoaded = React.useCallback(() => {
    setCompletedBookingsLoaded(false);
  }, []);

  return (
    <StateContext.Provider
      value={{
        ...contextValue,
        getUpcomingBookings,
        getInProgressBookings,
        getCompletedBookings,
        getBooking,
        loadPartnerCoverage,
        updatePartnerCoverage,
        updatePartner,
        getIncome,
        runningLate, // setRunningLate,
        rateCustomer, // addCustomerRating,
        startSession,
        stopSession,
        declineSession,
        cancelSession,
        getAvailability,
        getBookedTimeSlots,
        updateAvailability,
        recoverPassword,
        locations,
        sendResetCode,
        verifyPhoneNumberPasswordReset,
        resetPassword,
        clearCompletedBookingsLoaded,
      }}
    >
      {props.children}
    </StateContext.Provider>
  );
}

export function useAppState() {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useAppState must be used within the AppStateProvider');
  }
  return context;
}
